/**
 * Image Upload Service
 * 
 * Simplified approach: Store Google Drive URLs directly in note content,
 * cache base64 images in IndexedDB for offline support and cross-browser compatibility.
 * 
 * @author SFG Java Developers
 * @version 2.0.0
 */

import { processImageForEditor } from '../utils/imageCompression'
import localStorageService from './localStorageService'

/**
 * Upload and process image file
 * Returns Google Drive URL for direct use in note content
 */
export const uploadImageFile = async (file, isLocalMode = true) => {
  if (!file) {
    throw new Error('No file provided')
  }

  // Validate file type
  if (!file.type.startsWith('image/')) {
    throw new Error('Please select a valid image file')
  }

  // Validate file size (50MB limit - we'll compress it anyway)
  if (file.size > 50 * 1024 * 1024) {
    throw new Error('File size too large. Maximum allowed size is 50MB.')
  }

  try {
    console.log('🖼️ Processing image file:', file.name, file.size, 'bytes')
    
    // Process and compress image
    const compressionResult = await processImageForEditor(file, {
      maxSizeKB: 200,
      compressionQuality: 0.85,
      maxWidth: 2560,
      maxHeight: 1440,
      enableSharpening: false
    })
    
    if (!compressionResult.success) {
      throw new Error(`Image processing failed: ${compressionResult.error}`)
    }

    // Create a new File object from the compressed data URL
    const compressedFile = await dataURLToFile(compressionResult.dataUrl, file.name)
    
    let uploadResult
    if (isLocalMode) {
      // Local mode - save to IndexedDB
      uploadResult = await localStorageService.saveImageAttachment(compressedFile)
      
      return {
        success: true,
        attachmentId: uploadResult.id,
        fileName: uploadResult.fileName,
        originalFileName: file.name,
        fileType: uploadResult.fileType,
        fileSize: uploadResult.fileSize,
        filePath: uploadResult.filePath,
        originalSize: compressionResult.originalSize,
        compressedSize: compressionResult.compressedSize,
        wasCompressed: compressionResult.wasCompressed,
        compressionRatio: compressionResult.compressionRatio,
        isCloudFile: false,
        // For local mode, we'll use data URLs directly
        imageUrl: null
      }
    } else {
      // Cloud mode - upload to Google Drive
      if (!window.googleDriveAPI || !window.googleDriveAPI.accessToken) {
        throw new Error('Google Drive API not available or not authenticated. Please reconnect to Google Drive.')
      }
      
      // Generate consistent filename: baseFileName_timestamp.extension
      const baseFileName = file.name.replace(/\.[^/.]+$/, '') // Remove extension
      const extension = file.name.split('.').pop()
      const timestamp = Date.now()
      const uniqueFileName = `${baseFileName}_${timestamp}.${extension}`
      
      // Always save locally first for offline access
      const localResult = await localStorageService.saveImageAttachment(compressedFile)
      console.log('🖼️ Local save result:', localResult)
      
      try {
        // Upload to Google Drive
        const cloudResult = await window.googleDriveAPI.uploadAttachment(compressedFile)
        console.log('🖼️ Cloud upload result:', cloudResult)
        
        // Create Google Drive URL for direct use in note content
        const googleDriveUrl = `https://drive.google.com/file/d/${cloudResult.id}/view`
        
        // Cache the base64 image in IndexedDB for offline support
        await cacheImageBase64(cloudResult.id, compressedFile, file.name)
        
        uploadResult = {
          ...cloudResult,
          localId: localResult.id,
          actualFileName: uniqueFileName,
          originalFileName: file.name,
          googleDriveUrl: googleDriveUrl
        }
        
        console.log('🖼️ Final upload result (cloud):', uploadResult)
      } catch (cloudError) {
        console.warn('⚠️ Cloud upload failed, using local storage only:', cloudError.message)
        
        // Fallback to local storage only
        uploadResult = {
          id: localResult.id,
          fileName: file.name,
          originalFileName: file.name,
          actualFileName: uniqueFileName,
          fileType: compressedFile.type,
          fileSize: compressedFile.size,
          localId: localResult.id,
          isCloudFile: false,
          googleDriveUrl: null
        }
        
        console.log('🖼️ Final upload result (local fallback):', uploadResult)
      }
    }
    
    console.log('✅ Image uploaded successfully:', uploadResult.id || uploadResult.actualFileName)
    
    return {
      success: true,
      attachmentId: uploadResult.id,
      fileName: uploadResult.fileName || uploadResult.actualFileName,
      actualFileName: uploadResult.actualFileName,
      originalFileName: uploadResult.originalFileName || file.name,
      fileType: uploadResult.fileType || compressedFile.type,
      fileSize: uploadResult.fileSize || compressedFile.size,
      filePath: uploadResult.filePath,
      originalSize: compressionResult.originalSize,
      compressedSize: compressionResult.compressedSize,
      wasCompressed: compressionResult.wasCompressed,
      compressionRatio: compressionResult.compressionRatio,
      isCloudFile: !isLocalMode,
      localId: uploadResult.localId,
      googleDriveUrl: uploadResult.googleDriveUrl // Google Drive URL for note content
    }
    
  } catch (error) {
    console.error('❌ Image upload failed:', error)
    throw error
  }
}

/**
 * Cache image as base64 in IndexedDB by Google Drive file ID
 */
const cacheImageBase64 = async (fileId, file, originalFileName) => {
  try {
    // Convert file to base64
    const base64Data = await fileToBase64(file)
    
    // Store in IndexedDB with file ID as key
    const cacheData = {
      fileId: fileId,
      base64Data: base64Data,
      fileName: originalFileName,
      fileType: file.type,
      fileSize: file.size,
      cachedAt: new Date().toISOString()
    }
    
    // Store in a new IndexedDB store for image cache
    await localStorageService.storeImageCache(fileId, cacheData)
    
    console.log('✅ Image cached as base64:', {
      fileId,
      fileName: originalFileName,
      cacheSize: base64Data.length
    })
  } catch (error) {
    console.error('❌ Failed to cache image base64:', error)
    // Don't throw - caching is optional
  }
}

/**
 * Get cached base64 image by Google Drive file ID
 */
export const getCachedImageBase64 = async (fileId) => {
  try {
    return await localStorageService.getImageCache(fileId)
  } catch (error) {
    console.warn('⚠️ Failed to get cached image:', error)
    return null
  }
}

/**
 * Get or fetch image as base64 for display
 * First checks IndexedDB cache, then fetches from Google Drive if needed
 */
export const getImageBase64 = async (googleDriveUrl, fileId) => {
  try {
    // First, try to get from cache
    const cachedImage = await getCachedImageBase64(fileId)
    if (cachedImage && cachedImage.base64Data) {
      console.log('✅ Using cached base64 image:', fileId, 'Size:', cachedImage.base64Data.length)
      return cachedImage.base64Data
    } else if (cachedImage) {
      console.warn('⚠️ Cached image found but no base64Data:', cachedImage)
      // Try to clean up the incomplete cache entry
      try {
        await localStorageService.deleteImageCache(fileId)
        console.log('🧹 Cleaned up incomplete cache entry for:', fileId)
      } catch (cleanupError) {
        console.warn('⚠️ Failed to clean up incomplete cache entry:', cleanupError)
      }
    } else {
      console.log('⚠️ No cached image found for:', fileId)
    }
    
    // If not in cache, fetch from Google Drive
    if (!window.googleDriveAPI || !window.googleDriveAPI.accessToken) {
      throw new Error('Google Drive API not available')
    }
    
    console.log('🔄 Fetching image from Google Drive:', fileId)
    const cloudFile = await window.googleDriveAPI.downloadAttachment(fileId)
    
    if (!cloudFile || !cloudFile.blob) {
      throw new Error('Failed to download image from Google Drive')
    }
    
    // Convert blob to base64
    const base64Data = await blobToBase64(cloudFile.blob)
    
    // Check again if cache was populated by another concurrent call
    const existingCache = await getCachedImageBase64(fileId)
    if (!existingCache || !existingCache.base64Data) {
      const cacheData = {
        fileId: fileId,
        base64Data: base64Data,
        fileName: cloudFile.fileName || 'image',
        fileType: cloudFile.fileType || cloudFile.blob.type,
        fileSize: cloudFile.fileSize || cloudFile.blob.size,
        cachedAt: new Date().toISOString()
      }
      
      await localStorageService.storeImageCache(fileId, cacheData)
      console.log('✅ Fetched and cached image from Google Drive:', fileId)
    } else {
      console.log('✅ Fetched image from Google Drive (preserving existing cache):', fileId)
    }
    
    return base64Data
    
  } catch (error) {
    console.error('❌ Failed to get image base64:', error)
    throw error
  }
}

/**
 * Pre-cache images from a note's content when online
 * This helps ensure images are available when offline
 */
export const preCacheImagesFromContent = async (content) => {
  if (!navigator.onLine || !window.googleDriveAPI || !window.googleDriveAPI.accessToken) {
    console.log('⚠️ Skipping image pre-caching - offline or no API access')
    return
  }

  try {
    const googleDriveUrlRegex = /https:\/\/drive\.google\.com\/file\/d\/([a-zA-Z0-9-_]+)\/view/g
    let match
    const imageIds = []

    // Extract all Google Drive image IDs from content
    while ((match = googleDriveUrlRegex.exec(content)) !== null) {
      const fileId = match[1]
      imageIds.push(fileId)
    }

    if (imageIds.length === 0) {
      return
    }

    console.log(`🖼️ Pre-caching ${imageIds.length} images for offline use...`)

    // Pre-cache each image
    for (const fileId of imageIds) {
      try {
        // Check if already cached
        const cachedImage = await getCachedImageBase64(fileId)
        if (cachedImage && cachedImage.base64Data) {
          console.log(`✅ Image already cached: ${fileId}`)
          continue
        }

        // Fetch and cache the image
        const cloudFile = await window.googleDriveAPI.downloadAttachment(fileId)
        if (cloudFile && cloudFile.blob) {
          const base64Data = await blobToBase64(cloudFile.blob)
          
          const cacheData = {
            fileId: fileId,
            base64Data: base64Data,
            fileName: cloudFile.fileName || 'image',
            fileType: cloudFile.fileType || cloudFile.blob.type,
            fileSize: cloudFile.fileSize || cloudFile.blob.size,
            cachedAt: new Date().toISOString()
          }
          
          await localStorageService.storeImageCache(fileId, cacheData)
          console.log(`✅ Pre-cached image: ${fileId}`)
        }
      } catch (error) {
        console.warn(`⚠️ Failed to pre-cache image ${fileId}:`, error)
        // Continue with other images
      }
    }

    console.log(`✅ Pre-caching completed for ${imageIds.length} images`)
  } catch (error) {
    console.error('❌ Failed to pre-cache images:', error)
  }
}

/**
 * Convert File object to base64
 */
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

/**
 * Convert Blob to base64
 */
const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

/**
 * Convert data URL to File object
 */
const dataURLToFile = (dataUrl, fileName) => {
  return new Promise((resolve, reject) => {
    try {
      // Extract the base64 data and MIME type
      const arr = dataUrl.split(',')
      const mime = arr[0].match(/:(.*?);/)[1]
      const bstr = atob(arr[1])
      let n = bstr.length
      const u8arr = new Uint8Array(n)
      
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n)
      }
      
      // Create File object
      const file = new File([u8arr], fileName, { type: mime })
      resolve(file)
    } catch (error) {
      reject(error)
    }
  })
}

/**
 * Extract Google Drive file ID from URL
 */
export const extractFileIdFromUrl = (googleDriveUrl) => {
  const match = googleDriveUrl.match(/\/file\/d\/([a-zA-Z0-9-_]+)\//)
  return match ? match[1] : null
}

export default {
  uploadImageFile,
  getCachedImageBase64,
  getImageBase64,
  extractFileIdFromUrl
}